import LABTrainingPeaksSummary from './LABTrainingPeaksSummary';
// src/components/Strength/StrengthActivityModal.js
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import StrengthDetailsForm from '../Strength/StrengthDetailsForm';
import StrengthActions from '../Strength/StrengthActions';
import WellnessForm from '../ActivityDetails/WellnessForm';
import PlannedVsCompletedStrength from '../Strength/PlannedVsCompletedStrength';
import StrengthSummary from '../Strength/StrengthSummary';
import StrengthPhysiologicalAnalysis from '../Strength/StrengthPhysiologicalAnalysis';
import FileUploadComponent from '../FileUploadComponent';
import StrengthMetricsProcessor from '../Strength/StrengthMetricsProcessor';
import StrengthHeartRateHistogram from '../Strength/StrengthHeartRateHistogram';
import ZoneDistributionChartStrength from '../Strength/ZoneDistributionChartStrength';
import '../styles/StrengthActivityModal.css';
import { useTranslation } from '../../i18n';
import moment from 'moment';
import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import '../styles/ActivityDetailsModal.css';
import SessionAiSummaryPanel from '../Insights/SessionAiSummaryPanel';

/* ---------------- Helpers ---------------- */
const pad = (n) => String(n).padStart(2, '0');
const normalizeType = (raw = '') => {
  const t = String(raw).toLowerCase();
  if (t.includes('nad')) return 'nadar';
  if (t.includes('cicl') || t.includes('bike')) return 'ciclismo';
  if (t.includes('fuer') || t.includes('strength') || t.includes('gym')) return 'fuerza';
  if (t.includes('cam') || t.includes('walk')) return 'caminar';
  if (t.includes('desc') || t.includes('rest')) return 'descanso';
  if (t.includes('cor') || t.includes('run')) return 'correr';
  return 'otro';
};
const toHHMMSS = (input) => {
  if (!input && input !== 0) return '';
  const s = String(input).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const [h, m, sec = '00'] = s.split(':');
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  }
  if (/^\d+$/.test(s)) {
    const secs = parseInt(s, 10);
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), sec = secs % 60;
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  }
  let total = 0, m;
  const re = /(\d+(?:\.\d+)?)\s*(h|hr|hora|horas|m|min|minutos|s|seg|segundos)/gi;
  while ((m = re.exec(s))) {
    const val = parseFloat(m[1]); const u = m[2].toLowerCase();
    if (['h','hr','hora','horas'].includes(u)) total += val * 3600;
    else if (['m','min','minutos'].includes(u)) total += val * 60;
    else total += val;
  }
  return total ? toHHMMSS(String(Math.round(total))) : '';
};
const durationToSeconds = (hhmmss = '') => {
  const [h = '0', m = '0', s = '0'] = String(hhmmss).split(':');
  return (+h) * 3600 + (+m) * 60 + (+s);
};
const computeEnd = (startIso, hhmmss) => {
  if (!startIso || !hhmmss) return startIso;
  const secs = durationToSeconds(hhmmss);
  return new Date(new Date(startIso).getTime() + secs * 1000).toISOString();
};
const resolveAssignedTo = (explicit) =>
  explicit || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');

const initialActivityState = {
  title: '', description: '', preComments: '', postComments: '',
  planned: {}, completed: {}, metrics: {}
};

const StrengthActivityModal = ({
  show, onHide, activityType, onSave, onDelete,
  fetchEvents, profilePicture, profileData, selectedDate
}) => {
  const { t } = useTranslation();
  const [activity, setActivity]         = useState(initialActivityState);
  const [summary, setSummary]           = useState(null);
  const prevIdRef                       = useRef(null);
  const [latchedFileId, setLatchedFileId]     = useState(null);
  const [latchedSummary, setLatchedSummary]   = useState(null);

  const effectiveFileId =
    latchedFileId ||
    activity?.completed?.fileId ||
    activity?.fileId ||
    activityType?.completed?.fileId ||
    activityType?.fileId ||
    null;

  const effectiveUserId =
    activity?.assignedTo ||
    activityType?.assignedTo ||
    resolveAssignedTo();

  const handleSave = () => {
    const isPlannedFilled = Object.values(activity.planned || {}).some((val) => val && val.toString().trim() !== '');
    const hasDescription  = activity.description?.trim() !== '';
    if (!isPlannedFilled && !hasDescription) {
      alert(t('strength.validationPlanned'));
      return;
    }
    const assignedTo = resolveAssignedTo(activity.assignedTo || activityType?.assignedTo);
    if (!assignedTo) {
      alert(t('activityDetails.validationAthleteNotFound'));
      return;
    }
    const startIso = new Date(selectedDate || new Date()).toISOString();
    const dur      = toHHMMSS(activity.planned?.duracion || activity.duration || '');
    const endIso   = dur ? computeEnd(startIso, dur) : startIso;
    const payload  = {
      ...activity, assignedTo,
      type: normalizeType(activity.type || activityType?.type || 'fuerza'),
      date: startIso, start: startIso, end: endIso,
      duration: dur || '',
      planned: { ...(activity.planned || {}), duracion: dur || '' },
      bgColor: activity.bgColor || '#37474F',
      external: !!activity.external
    };
    if (typeof onSave === 'function') onSave(payload);
    onHide();
  };

  const handleDelete = () => {
    if (typeof onDelete === 'function' && activityType?.id) {
      onDelete(activityType.id);
      onHide();
    }
  };

  const handleFileUpload = async (data) => {
    if (data.fileId) {
      setLatchedFileId(data.fileId);
      setActivity((prev) => ({ ...prev, completed: { ...prev.completed, fileId: data.fileId } }));
    }
  };

  useEffect(() => {
    // Use 'new' as sentinel so new activities (no id) are not skipped by the guard
    const currentId = activityType?.id ?? activityType?._id ?? 'new';
    if (prevIdRef.current === currentId && currentId !== 'new') return;
    prevIdRef.current = currentId;

    setLatchedFileId(null);
    setLatchedSummary(null);

    if (!activityType) {
      setActivity(initialActivityState);
      return;
    }

    const planned = {
      ...(activityType.planned || {}),
      duracion: toHHMMSS(
        activityType?.planned?.duracion ||
        activityType?.planned?.duration ||
        activityType?.duration ||
        ''
      ),
    };

    const updatedActivity = {
      ...initialActivityState,
      ...activityType,
      title: activityType?.title || t('activityLabels.strength'),
      planned,
      completed: { ...(activityType.completed || {}) },
      metrics: activityType.metrics || {}
    };

    const fileFromProps = activityType?.completed?.fileId || activityType?.fileId || null;
    if (fileFromProps) {
      updatedActivity.completed.fileId = fileFromProps;
      setLatchedFileId(fileFromProps);
    }

    setActivity(updatedActivity);
  }, [activityType, activityType?.id, activityType?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes so next open always re-initializes
  useEffect(() => {
    if (!show) {
      prevIdRef.current = null;
      setLatchedFileId(null);
      setLatchedSummary(null);
      setSummary(null);
      setActivity(initialActivityState);
    }
  }, [show]);

  const handleSummary = ({ session, completed, laps, records, metrics }) => {
    const cleanedCompleted = {
      ...completed,
      tea: completed.tea === undefined || completed.tea === null || completed.tea === '' || completed.tea === '-'
        ? 0 : parseFloat(completed.tea)
    };
    const fidFromSummary = cleanedCompleted.fileId || effectiveFileId;
    if (fidFromSummary) setLatchedFileId(fidFromSummary);

    setActivity((prev) => ({
      ...prev,
      completed: { fileId: fidFromSummary, ...prev.completed, ...cleanedCompleted },
      metrics
    }));

    const packed = { session, completed: cleanedCompleted, laps, records, metrics };
    setSummary(packed);
    setLatchedSummary(packed);
  };

  const summaryToShow = latchedSummary || summary;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton className="tz20-activity-header">
        <div className="tz20-activity-title-wrap">
          <span className="tz20-activity-icon" aria-hidden="true"><img src={strengthIcon} alt="" /></span>
          <div>
            <Modal.Title className="tz20-activity-title">
              {t('activityDetails.activityModalTitle', { title: activity.title || t('activityLabels.strength') })}
            </Modal.Title>
            <div className="tz20-activity-meta">
              {activity?.date ? moment(activity.date).format('ddd D MMM YYYY') : moment().format('ddd D MMM YYYY')}
              {' · '}
              {activity?.completed?.fileId ? 'Archivo .fit cargado' : 'Sin archivo .fit'}
            </div>
          </div>
        </div>
        <div className="tz20-sport-tabs" aria-label="Deportes">
          <button type="button" aria-label="Correr"><img src={runningIcon} alt="" /></button>
          <button type="button" aria-label="Ciclismo"><img src={cyclingIcon} alt="" /></button>
          <button type="button" aria-label="Nadar"><img src={swimIcon} alt="" /></button>
          <button type="button" className="active" aria-label="Fuerza"><img src={strengthIcon} alt="" /></button>
        </div>
      </Modal.Header>

      <Modal.Body className="tz20-activity-body">
          <Row className="align-items-start mt-4">
            <Col md={6}>
              <StrengthDetailsForm
                title={activity.title}
                description={activity.description}
                preComments={activity.preComments}
                postComments={activity.postComments}
                setTitle={(title) => setActivity((prev) => ({ ...prev, title }))}
                setDescription={(description) => setActivity((prev) => ({ ...prev, description }))}
                setPreComments={(preComments) => setActivity((prev) => ({ ...prev, preComments }))}
                setPostComments={(postComments) => setActivity((prev) => ({ ...prev, postComments }))}
              />
            </Col>
            <Col md={6}>
              <PlannedVsCompletedStrength
                planned={activity.planned}
                completed={activity.completed}
                setPlanned={(newPlanned) => setActivity((prev) => ({ ...prev, planned: newPlanned }))}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col>
              
          {!activity.completed?.fileId && (
            <WellnessForm
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              date={activity?.date || activityType?.date}
            />
          )}
          <FileUploadComponent activityId={activityType?.id || 'undefined'} activityType="fuerza" onFileUpload={handleFileUpload} />
            </Col>
          </Row>

          {effectiveFileId && (
            <StrengthMetricsProcessor fileId={effectiveFileId} onSummaryData={handleSummary} />
          )}

          {effectiveFileId && summaryToShow?.completed && (activityType?.id || effectiveUserId) && (
            <>
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('strength.activitySummary')}</h2>
                <StrengthSummary fileId={effectiveFileId} preLoadedData={summaryToShow} ftp={summaryToShow?.metrics?.ftp} />
              </div>
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('elevationProfile.hrTimeChartTitle')}</h2>
                <StrengthHeartRateHistogram fileId={effectiveFileId} />
              </div>
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('strength.zoneDistribution')}</h2>
                <ZoneDistributionChartStrength userId={effectiveUserId} fileId={effectiveFileId} />
              </div>
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('strength.physiologicalAnalysis')}</h2>
                <StrengthPhysiologicalAnalysis
                  completed={summaryToShow?.completed}
                  metrics={summaryToShow?.metrics}
                  athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
                  activityId={activityType?.id}
                  date={activity?.date || activityType?.date}
                />
              </div>
            </>
          )}

          {activity.completed?.fileId && (
            <WellnessForm
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              date={activity?.date || activityType?.date}
            />
          )}
          <SessionAiSummaryPanel
            sport="strength"
            athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
            activityId={activityType?.id}
            date={activity?.date || activityType?.date}
            completed={summaryToShow?.completed}
            metrics={summaryToShow?.metrics}
          />
        </Modal.Body>

        <LABTrainingPeaksSummary activityId={activityType?.id || activityType?._id} />
      <Modal.Footer>
        <StrengthActions onSave={handleSave} onDelete={handleDelete} onHide={onHide} />
      </Modal.Footer>
    </Modal>
  );
};

export default StrengthActivityModal;
